import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Paperclip, X, Upload, Globe, Check, Bus, BedDouble, Plus } from "lucide-react";
import { toast } from "sonner";
import { TRANSPORT_OPTIONS, prettyDate, formatDate, parseDate } from "@/lib/addons";

export const COLOR_PALETTE = [
    { value: "#F0F0EE", label: "Stone" },
    { value: "#E8DDD0", label: "Sand" },
    { value: "#DCE4D8", label: "Sage" },
    { value: "#D8DEE5", label: "Mist" },
    { value: "#E8DCD9", label: "Clay" },
    { value: "#E0DCE8", label: "Lilac" },
];

function toLocalInput(d) {
    if (!d) return "";
    const dt = new Date(d);
    const pad = n => String(n).padStart(2, "0");
    return `${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
}

export default function CreateActivityDialog({ open, onOpenChange, initialDate, activity, onSaved }) {
    const isEdit = !!activity;
    const fileInputRef = useRef();
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        title: "", description: "", location: "", tag: "",
        start_time: "", end_time: "",
        max_signups: 0, attachments: [],
        color: "#F0F0EE", is_public: false,
        signup_deadline: "",
        reminder_at: "",
        offer_transport: false,
        transport_options: [],
        offer_accommodation: false,
        accommodation_dates: [],
    });

    useEffect(() => {
        if (!open) return;
        if (isEdit) {
            setForm({
                title: activity.title,
                description: activity.description || "",
                location: activity.location || "",
                tag: activity.tag || "",
                start_time: toLocalInput(activity.start_time),
                end_time: toLocalInput(activity.end_time),
                max_signups: activity.max_signups || 0,
                attachments: activity.attachments || [],
                color: activity.color || "#F0F0EE",
                is_public: !!activity.is_public,
                signup_deadline: activity.signup_deadline ? toLocalInput(activity.signup_deadline) : "",
                reminder_at: activity.reminder_at ? toLocalInput(activity.reminder_at) : "",
                offer_transport: !!activity.offer_transport,
                transport_options: activity.transport_options || [],
                offer_accommodation: !!activity.offer_accommodation,
                accommodation_dates: activity.accommodation_dates || [],
            });
        } else {
            const base = initialDate ? new Date(initialDate) : new Date();
            base.setHours(12, 0, 0, 0);
            const end = new Date(base.getTime() + 60 * 60 * 1000);
            setForm({
                title: "", description: "", location: "", tag: "",
                start_time: toLocalInput(base), end_time: toLocalInput(end),
                max_signups: 0, attachments: [],
                color: "#F0F0EE", is_public: false,
                signup_deadline: "",
                reminder_at: "",
                offer_transport: false,
                transport_options: [],
                offer_accommodation: false,
                accommodation_dates: [],
            });
        }
    }, [open, activity, initialDate, isEdit]);

    const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []);
        if (!files.length) return;
        setBusy(true);
        try {
            const uploaded = [];
            for (const f of files) {
                const fd = new FormData();
                fd.append("file", f);
                const { data } = await api.post("/upload", fd, {
                    headers: { "Content-Type": "multipart/form-data" },
                });
                uploaded.push(data);
            }
            setField("attachments", [...form.attachments, ...uploaded]);
            toast.success(`${uploaded.length} file${uploaded.length>1?"s":""} uploaded`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally {
            setBusy(false);
        }
    };

    const removeAttachment = (id) => {
        setField("attachments", form.attachments.filter(a => a.id !== id));
    };

    const submit = async () => {
        if (!form.title.trim()) return toast.error("Title is required");
        if (!form.start_time || !form.end_time) return toast.error("Start and end times required");
        if (new Date(form.end_time) <= new Date(form.start_time)) return toast.error("End must be after start");
        if (form.offer_transport && form.transport_options.length === 0) {
            return toast.error("Pick at least one transport option, or turn Transportation off");
        }
        if (form.offer_accommodation && form.accommodation_dates.length === 0) {
            return toast.error("Add at least one accommodation date, or turn Accommodation off");
        }

        setBusy(true);
        try {
            const payload = {
                title: form.title.trim(),
                description: form.description,
                location: form.location,
                tag: form.tag,
                start_time: new Date(form.start_time).toISOString(),
                end_time: new Date(form.end_time).toISOString(),
                max_signups: Number(form.max_signups) || 0,
                attachments: form.attachments,
                reminder_minutes: 0,
                color: form.color,
                is_public: form.is_public,
                signup_deadline: form.signup_deadline ? new Date(form.signup_deadline).toISOString() : null,
                offer_transport: form.offer_transport,
                transport_options: form.offer_transport ? form.transport_options : [],
                offer_accommodation: form.offer_accommodation,
                accommodation_dates: form.offer_accommodation ? form.accommodation_dates : [],
            };
            if (isEdit) {
                await api.put(`/activities/${activity.id}`, payload);
                toast.success("Activity updated");
            } else {
                await api.post("/activities", payload);
                toast.success("Activity created");
            }
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl rounded-none border-[#E5E5E3] p-0 shadow-none max-h-[90vh] grid-rows-[1fr_auto] overflow-hidden" data-testid="activity-dialog">
                <div className="p-8 overflow-y-auto">
                    <DialogHeader className="text-left space-y-1">
                        <p className="text-[10px] tracking-[0.2em] uppercase text-[#888885]">{isEdit ? "Edit" : "New"} activity</p>
                        <DialogTitle className="font-heading text-2xl sm:text-3xl font-light tracking-tight text-[#111110]">
                            {isEdit ? "Update the details" : "What are we doing?"}
                        </DialogTitle>
                        <DialogDescription className="text-sm text-[#666664]">
                            Create a shared moment. Anyone at scratchfirst can sign up.
                        </DialogDescription>
                    </DialogHeader>

                    <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="md:col-span-2">
                            <FieldLabel>Title</FieldLabel>
                            <Input value={form.title} onChange={e => setField("title", e.target.value)}
                                placeholder="e.g. Friday run club" data-testid="input-title"
                                className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                        </div>
                        <div>
                            <FieldLabel>Starts</FieldLabel>
                            <Input type="datetime-local" value={form.start_time}
                                onChange={e => setField("start_time", e.target.value)} data-testid="input-start"
                                className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                        </div>
                        <div>
                            <FieldLabel>Ends</FieldLabel>
                            <Input type="datetime-local" value={form.end_time}
                                onChange={e => setField("end_time", e.target.value)} data-testid="input-end"
                                className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                        </div>
                        <div>
                            <FieldLabel>Location</FieldLabel>
                            <Input value={form.location} onChange={e => setField("location", e.target.value)}
                                placeholder="Main Kitchen / Zoom" data-testid="input-location"
                                className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                        </div>
                        <div>
                            <FieldLabel>Tag</FieldLabel>
                            <Input value={form.tag} onChange={e => setField("tag", e.target.value)}
                                placeholder="social, learning, sports…" data-testid="input-tag"
                                className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                        </div>
                        <div className="md:col-span-2">
                            <FieldLabel>Color</FieldLabel>
                            <div className="flex flex-wrap gap-2" data-testid="color-picker">
                                {COLOR_PALETTE.map(c => {
                                    const active = form.color === c.value;
                                    return (
                                        <button
                                            type="button"
                                            key={c.value}
                                            onClick={() => setField("color", c.value)}
                                            data-testid={`color-${c.label.toLowerCase()}`}
                                            className={`group relative w-11 h-11 border transition-all ${active ? "border-[#111110]" : "border-[#E5E5E3] hover:border-[#888885]"}`}
                                            style={{ backgroundColor: c.value }}
                                            title={c.label}
                                        >
                                            {active && <Check className="w-4 h-4 text-[#111110] absolute inset-0 m-auto" strokeWidth={2} />}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <FieldLabel>Description</FieldLabel>
                            <Textarea value={form.description} onChange={e => setField("description", e.target.value)}
                                placeholder="What to expect, what to bring…" data-testid="input-description"
                                className="rounded-none border-[#E5E5E3] min-h-[100px] focus-visible:ring-1 focus-visible:ring-[#111110]" />
                        </div>
                        <div>
                            <FieldLabel>Max sign-ups <span className="text-[#888885] normal-case tracking-normal"> (0 = unlimited)</span></FieldLabel>
                            <Input type="number" min="0" value={form.max_signups}
                                onChange={e => setField("max_signups", e.target.value)} data-testid="input-max"
                                className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                        </div>
                        <div>
                            <FieldLabel>RSVP by <span className="text-[#888885] normal-case tracking-normal"> (optional)</span></FieldLabel>
                            <Input type="datetime-local" value={form.signup_deadline}
                                onChange={e => setField("signup_deadline", e.target.value)} data-testid="input-deadline"
                                className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                        </div>
                        <div className="md:col-span-2">
                            <FieldLabel>Send reminder <span className="text-[#888885] normal-case tracking-normal"> (optional — attendees + guests)</span></FieldLabel>
                            <Input type="datetime-local" value={form.reminder_at}
                                onChange={e => setField("reminder_at", e.target.value)} data-testid="input-reminder-at"
                                className="rounded-none border-[#E5E5E3] h-11 focus-visible:ring-1 focus-visible:ring-[#111110]" />
                        </div>
                        <div>
                            <FieldLabel>Attachments</FieldLabel>
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                className="hidden"
                                onChange={e => handleFiles(e.target.files)}
                                data-testid="file-input"
                            />
                            <Button type="button" variant="outline"
                                onClick={() => fileInputRef.current?.click()}
                                disabled={busy}
                                className="w-full h-11 rounded-none border-[#E5E5E3] text-[#111110] hover:bg-[#F0F0EE]"
                                data-testid="upload-file-btn">
                                <Upload className="w-4 h-4 mr-2" strokeWidth={1.5} /> Add files
                            </Button>
                        </div>

                        {form.attachments.length > 0 && (
                            <div className="md:col-span-2">
                                <ul className="space-y-2" data-testid="attachments-list">
                                    {form.attachments.map(a => (
                                        <li key={a.id} className="border border-[#E5E5E3] p-3 flex items-center gap-3 text-sm">
                                            <Paperclip className="w-4 h-4 text-[#666664]" strokeWidth={1.5} />
                                            <span className="flex-1 truncate text-[#111110]">{a.original_filename}</span>
                                            <button onClick={() => removeAttachment(a.id)} className="text-[#888885] hover:text-[#111110]" data-testid={`remove-attachment-${a.id}`}>
                                                <X className="w-4 h-4" strokeWidth={1.5} />
                                            </button>
                                        </li>
                                    ))}                                </ul>
                            </div>
                        )}

                        <div className="md:col-span-2 border border-[#E5E5E3] p-4 flex items-start gap-3">
                            <button
                                type="button"
                                onClick={() => setField("is_public", !form.is_public)}
                                aria-pressed={form.is_public}
                                data-testid="toggle-public"
                                className={`shrink-0 mt-0.5 w-10 h-6 border transition-colors ${form.is_public ? "bg-[#111110] border-[#111110]" : "bg-white border-[#E5E5E3]"}`}
                            >
                                <span className={`block w-4 h-4 m-0.5 transition-transform ${form.is_public ? "translate-x-4 bg-white" : "translate-x-0 bg-[#111110]"}`} />
                            </button>
                            <div className="flex-1">
                                <div className="flex items-center gap-2">
                                    <Globe className="w-4 h-4 text-[#666664]" strokeWidth={1.5} />
                                    <span className="text-sm font-medium text-[#111110]">Public RSVP link</span>
                                </div>
                                <p className="text-xs text-[#666664] mt-1 leading-relaxed">
                                    When enabled, anyone with the link can RSVP using their name, email, and phone — no login required. Useful for events with external guests.
                                </p>
                            </div>
                        </div>

                        {/* Offer Transportation */}
                        <div className="md:col-span-2 border border-[#E5E5E3] p-4 space-y-3">
                            <div className="flex items-start gap-3">
                                <button
                                    type="button"
                                    onClick={() => setField("offer_transport", !form.offer_transport)}
                                    aria-pressed={form.offer_transport}
                                    data-testid="toggle-transport"
                                    className={`shrink-0 mt-0.5 w-10 h-6 border transition-colors ${form.offer_transport ? "bg-[#111110] border-[#111110]" : "bg-white border-[#E5E5E3]"}`}
                                >
                                    <span className={`block w-4 h-4 m-0.5 transition-transform ${form.offer_transport ? "translate-x-4 bg-white" : "translate-x-0 bg-[#111110]"}`} />
                                </button>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <Bus className="w-4 h-4 text-[#666664]" strokeWidth={1.5} />
                                        <span className="text-sm font-medium text-[#111110]">Offer transportation</span>
                                    </div>
                                    <p className="text-xs text-[#666664] mt-1 leading-relaxed">
                                        Choose which trips are on offer. Attendees will pick one at sign-up, or opt out.
                                    </p>
                                </div>
                            </div>
                            {form.offer_transport && (
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-1" data-testid="transport-options-picker">
                                    {TRANSPORT_OPTIONS.map(opt => {
                                        const active = form.transport_options.includes(opt.value);
                                        return (
                                            <button
                                                type="button"
                                                key={opt.value}
                                                data-testid={`transport-opt-${opt.value}`}
                                                onClick={() => setField("transport_options",
                                                    active
                                                        ? form.transport_options.filter(v => v !== opt.value)
                                                        : [...form.transport_options, opt.value]
                                                )}
                                                aria-pressed={active}
                                                className={`px-3 py-2.5 border transition-colors flex items-center justify-between gap-2 text-sm ${
                                                    active ? "border-[#111110] bg-[#F0F0EE] text-[#111110]" : "border-[#E5E5E3] bg-white text-[#333333] hover:border-[#888885]"
                                                }`}
                                            >
                                                <span>{opt.label}</span>
                                                {active && <Check className="w-4 h-4" strokeWidth={2} />}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>

                        {/* Offer Accommodation */}
                        <div className="md:col-span-2 border border-[#E5E5E3] p-4 space-y-3">
                            <div className="flex items-start gap-3">
                                <button
                                    type="button"
                                    onClick={() => setField("offer_accommodation", !form.offer_accommodation)}
                                    aria-pressed={form.offer_accommodation}
                                    data-testid="toggle-accommodation"
                                    className={`shrink-0 mt-0.5 w-10 h-6 border transition-colors ${form.offer_accommodation ? "bg-[#111110] border-[#111110]" : "bg-white border-[#E5E5E3]"}`}
                                >
                                    <span className={`block w-4 h-4 m-0.5 transition-transform ${form.offer_accommodation ? "translate-x-4 bg-white" : "translate-x-0 bg-[#111110]"}`} />
                                </button>
                                <div className="flex-1">
                                    <div className="flex items-center gap-2">
                                        <BedDouble className="w-4 h-4 text-[#666664]" strokeWidth={1.5} />
                                        <span className="text-sm font-medium text-[#111110]">Offer accommodation</span>
                                    </div>
                                    <p className="text-xs text-[#666664] mt-1 leading-relaxed">
                                        Add the nights you&apos;re providing lodging for. Attendees pick which nights they need.
                                    </p>
                                </div>
                            </div>
                            {form.offer_accommodation && (
                                <AccommodationDatePicker
                                    dates={form.accommodation_dates}
                                    onChange={(d) => setField("accommodation_dates", d)}
                                    startDate={form.start_time}
                                />
                            )}
                        </div>
                    </div>
                </div>

                <DialogFooter className="border-t border-[#E5E5E3] px-8 py-5 bg-[#F9F9F8] flex-row gap-2 justify-end">
                    <Button variant="outline" onClick={() => onOpenChange(false)}
                        className="rounded-none border-[#E5E5E3] h-10 text-[#111110] hover:bg-[#F0F0EE]"
                        data-testid="cancel-dialog-btn">
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={busy}
                        className="rounded-none h-10 bg-[#111110] hover:bg-[#333333] text-white px-6"
                        data-testid="save-activity-btn">
                        {isEdit ? "Save changes" : "Create activity"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FieldLabel({ children }) {
    return <Label className="text-[10px] tracking-[0.2em] uppercase text-[#888885] mb-2 block font-medium">{children}</Label>;
}

function AccommodationDatePicker({ dates, onChange, startDate }) {
    // Simple: single date input + list of selected dates as removable chips.
    const [next, setNext] = useState("");

    // Seed the input with the activity's start date, if empty.
    const initial = (() => {
        if (!startDate) return "";
        const d = new Date(startDate);
        if (isNaN(d.getTime())) return "";
        return formatDate(d);
    })();

    const addDate = () => {
        const val = (next || initial).trim();
        if (!val) return;
        if (!parseDate(val)) return;
        if (dates.includes(val)) return;
        onChange([...dates, val].sort());
        setNext("");
    };

    const removeDate = (d) => onChange(dates.filter(x => x !== d));

    return (
        <div className="space-y-3" data-testid="accommodation-date-picker">
            <div className="flex flex-col sm:flex-row gap-2">
                <input
                    type="date"
                    value={next || initial}
                    onChange={e => setNext(e.target.value)}
                    data-testid="accommodation-date-input"
                    className="flex-1 rounded-none border border-[#E5E5E3] h-11 px-3 text-sm text-[#111110] focus:outline-none focus:ring-1 focus:ring-[#111110]"
                />
                <button
                    type="button"
                    onClick={addDate}
                    data-testid="accommodation-add-date-btn"
                    className="rounded-none border border-[#111110] bg-[#111110] text-white h-11 px-4 text-sm font-medium inline-flex items-center justify-center gap-1.5 hover:bg-[#333333]"
                >
                    <Plus className="w-4 h-4" strokeWidth={2} /> Add night
                </button>
            </div>
            {dates.length === 0 ? (
                <p className="text-xs text-[#888885]">No nights added yet.</p>
            ) : (
                <ul className="flex flex-wrap gap-2" data-testid="accommodation-dates-list">
                    {dates.map(d => (
                        <li key={d} className="border border-[#E5E5E3] bg-white px-3 py-1.5 text-sm text-[#111110] inline-flex items-center gap-2">
                            <span>{prettyDate(d)}</span>
                            <span className="text-[10px] text-[#888885] font-mono">{d}</span>
                            <button
                                type="button"
                                onClick={() => removeDate(d)}
                                aria-label={`Remove ${d}`}
                                data-testid={`accommodation-remove-${d}`}
                                className="text-[#888885] hover:text-[#111110]"
                            >
                                <X className="w-3.5 h-3.5" strokeWidth={1.5} />
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
